import { MODULES } from './modules';

// The entire Commercial Pilot Prep module set requires an active
// subscription (or granted/admin access) -- it's the paid product, not a
// free-tier teaser like the military track. Every military module's
// practice mode is free; only assessment mode is paywalled there (see
// ModuleMenu's assessmentLocked in cbtCommon.js, which applies site-wide).
// Derived from each module's own preparationFamily tag rather than a
// hand-maintained list, so a newly added commercial module is locked by
// default instead of silently free until someone remembers to list it here.
export const FREE_TIER_LOCKED_MODULE_IDS = MODULES
  .filter((module) => module.preparationFamily === 'commercial')
  .map((module) => module.id);

export const isLockedForFreeTier = (moduleId) => FREE_TIER_LOCKED_MODULE_IDS.includes(moduleId);

// A family's own subscriptions_enabled / commercial_subscriptions_enabled
// flag now does double duty: on means normal subscription-gated access for
// that family; off means every registered user in that family gets full
// access automatically (this replaces the old, single site-wide
// free_access_enabled flag, which incorrectly granted free access to BOTH
// families at once regardless of either family's own subscriptions
// setting). The two families' flags are completely independent -- toggling
// one can never affect the other. Defaults to "not free" (paid-gated) while
// siteSettings hasn't loaded yet, matching the safe pre-load default this
// function already used before accessState/siteSettings arrived.
const isFamilyFreeAccessEnabled = (siteSettings, activeFamily) => {
  if (!siteSettings) return false;
  const subscriptionsEnabledForFamily = activeFamily === 'commercial'
    ? siteSettings.commercial_subscriptions_enabled === true
    : siteSettings.subscriptions_enabled === true;
  return !subscriptionsEnabledForFamily;
};

// Paid access for this one account: an active or trialing subscription, a
// granted entitlement, or administrator status. Unlike hasFullAccess it
// ignores a family-wide free-access setting (subscriptions switched off for
// everyone), which is a site policy rather than this account having paid --
// mock tests use it, so they stay a subscription feature.
// Stripe's statuses for a subscription whose renewal payment failed: it's
// still there, retrying, but unpaid -- so it gives no paid access
// (hasPaidAccess) and has to be put right through Manage Billing rather
// than by starting another subscription.
export const PAYMENT_FAILED_STATUSES = ['past_due', 'unpaid'];

export const hasPaidAccess = (accessState, isAdmin = false) => {
  if (isAdmin) return true;
  if (!accessState) return false;
  return Boolean(
    accessState.subscription?.status === 'active'
    || accessState.subscription?.status === 'trialing'
    || accessState.entitlement
  );
};

export const hasFullAccess = (accessState, isAdmin = false, siteSettings = null, activeFamily = 'military') => {
  if (isAdmin) return true;
  if (isFamilyFreeAccessEnabled(siteSettings, activeFamily)) return true;
  return hasPaidAccess(accessState, isAdmin);
};

export const getEffectivePlanPresentation = (subscriptionState, accessState, isAdmin = false, siteSettings = null, activeFamily = 'military') => {
  if (isAdmin) {
    return {
      plan: 'Administrator Access',
      status: 'ACTIVE',
      source: 'admin',
      endsAt: null,
      cancelAtPeriodEnd: false
    };
  }

  // Bought permanent access. A live subscription alongside it still shows
  // first, so it can be managed (and cancelled) from the account page.
  const liveSubscription = ['active', 'trialing'].includes(String(subscriptionState?.subscription?.status || '').toLowerCase());
  if (accessState?.permanent && !liveSubscription) {
    return {
      plan: 'Permanent Access',
      status: 'PERMANENT ACCESS',
      source: accessState.entitlement?.source || 'stripe_payment',
      endsAt: null,
      cancelAtPeriodEnd: false,
      permanent: true
    };
  }

  if (isFamilyFreeAccessEnabled(siteSettings, activeFamily)) {
    return {
      plan: 'Full Access',
      status: 'ACTIVE',
      source: 'family_free_access',
      endsAt: null,
      cancelAtPeriodEnd: false
    };
  }

  const subscription = subscriptionState?.subscription;
  const subscriptionStatus = String(subscription?.status || '').toLowerCase();
  if (subscription && ['active', 'trialing'].includes(subscriptionStatus)) {
    return {
      plan: subscription.plan_name || 'Premium Subscription',
      status: subscriptionStatus.toUpperCase(),
      source: 'stripe',
      // cancel_at is Stripe's more general cancellation-scheduling field --
      // a subscription cancelled from the Stripe Dashboard can have this
      // set (matching its own current_period_end) while the legacy
      // cancel_at_period_end boolean stays false, which silently showed
      // "Renews" instead of "Access ending" for an account genuinely on
      // its way out. is_ending (from _serialize_subscription) already
      // covers both signals; cancel_at_period_end is read too only as a
      // fallback for a subscription object cached before that field existed.
      endsAt: subscription.cancel_at || subscription.current_period_end || null,
      cancelAtPeriodEnd: subscription.is_ending ?? Boolean(subscription.cancel_at_period_end)
    };
  }

  const entitlement = accessState?.entitlement;
  if (entitlement) {
    const source = entitlement.source || 'entitlement';
    const labels = {
      manual_grant: 'Granted Access',
      promo_code: 'Access Code',
      access_code: 'Access Code'
    };
    // A purchased one-time access package (source: 'stripe_payment') carries
    // its own plan_label (e.g. "1 Month") -- prefer that over the generic
    // source-keyed labels above, which are for admin grants and codes.
    return {
      plan: entitlement.plan_label || labels[source] || 'Granted Access',
      status: 'ACTIVE',
      source,
      endsAt: entitlement.ends_at || null,
      // An App Store subscription bought in the iOS app renews through Apple
      // unless its auto_renew flag says otherwise; other entitlements never
      // renew.
      cancelAtPeriodEnd: source === 'app_store' ? entitlement.auto_renew !== true : false
    };
  }

  // A subscription whose renewal payment failed. It's still the account's
  // Stripe subscription, so it keeps source 'stripe' -- which is what shows
  // Manage Billing (the Stripe portal, to update the card or cancel) and
  // hides the plan picker, rather than inviting a second subscription.
  if (subscription && PAYMENT_FAILED_STATUSES.includes(subscriptionStatus)) {
    return {
      plan: subscription.plan_name || 'Premium Subscription',
      status: 'PAYMENT FAILED',
      source: 'stripe',
      endsAt: null,
      cancelAtPeriodEnd: false,
      paymentFailed: true
    };
  }

  return {
    plan: 'Limited Access',
    status: 'LIMITED',
    source: 'free',
    endsAt: null,
    cancelAtPeriodEnd: false,
    // The previous subscription was ended by Stripe after its payments kept
    // failing (not cancelled by the customer).
    ...(subscription?.cancellation_reason === 'payment_failed' && subscriptionStatus === 'canceled'
      ? { subscriptionEndedByPayment: true }
      : {})
  };
};

// True only for a live, still-renewing subscription (Stripe, or the App Store
// from the iOS app) -- distinguishes "renews on {date}" from "access ends on
// {date}" wherever endsAt is shown. A one-time access package (entitlement)
// or a cancelled subscription never renews, so both read as "ends" instead.
export const isPlanRenewing = (effectivePlan) => Boolean(
  (effectivePlan?.source === 'stripe' || effectivePlan?.source === 'app_store')
  && effectivePlan?.cancelAtPeriodEnd === false
  // Not billing again until its payment is put right.
  && !effectivePlan?.paymentFailed
);

// Default warning window for ACCESS_ENDING_SOON_WINDOW_DAYS -- how close to
// its real end date access has to be before the "ending soon" banner shows.
export const ACCESS_ENDING_SOON_WINDOW_DAYS = 5;

// Null for anything that never ends (family free access, admin, a freely
// renewing Stripe subscription -- isPlanRenewing's own case) or that has no
// access at all (the "Limited Access" fallback, which carries no endsAt).
// Applies equally to a subscription on its way out and to any bounded
// entitlement (an access code, an admin grant, a one-time package) -- both
// read endsAt the same way, so this doesn't special-case source beyond
// excluding the never-ending ones above. endsAt is a Unix-seconds number
// for a Stripe subscription or an ISO string for an entitlement (see
// getEffectivePlanPresentation) -- both parse correctly below.
export const getAccessEndingSoon = (effectivePlan, thresholdDays = ACCESS_ENDING_SOON_WINDOW_DAYS) => {
  if (!effectivePlan?.endsAt) return null;
  if (effectivePlan.source === 'family_free_access' || effectivePlan.source === 'admin') return null;
  if (isPlanRenewing(effectivePlan)) return null;

  const endsAtMs = typeof effectivePlan.endsAt === 'number'
    ? effectivePlan.endsAt * 1000
    : Date.parse(effectivePlan.endsAt);
  if (!Number.isFinite(endsAtMs)) return null;

  const daysUntil = Math.ceil((endsAtMs - Date.now()) / (24 * 60 * 60 * 1000));
  if (daysUntil < 0 || daysUntil > thresholdDays) return null;

  return { daysUntil, endsAtMs };
};

// Describes a subscription tier's billing cadence from the Stripe
// interval/interval_count that /subscriptions/offer already surfaces per
// tier (see backend/server.py's _serialize_price_offer) -- a tier with no
// interval is a one-time payment (the permanent tier), not a subscription.
const SINGLE_INTERVAL_LABELS = { month: 'monthly', year: 'annually', week: 'weekly', day: 'daily' };

export const formatBillingInterval = (interval, intervalCount = 1) => {
  if (!interval) return 'One-time payment';
  const count = Number(intervalCount) || 1;
  if (count === 1) return `Billed ${SINGLE_INTERVAL_LABELS[interval] || `every ${interval}`}`;
  return `Billed every ${count} ${interval}s`;
};
